import { z } from 'zod';

/**
 * Repertório e arquivos (M11). A música tem tom base, e cada culto pode ter o
 * seu — a mesma canção muda de tom conforme quem canta.
 */

/** Os doze tons, na notação que a igreja brasileira usa. */
export const TONS = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
  'Cm',
  'C#m',
  'Dm',
  'D#m',
  'Em',
  'Fm',
  'F#m',
  'Gm',
  'G#m',
  'Am',
  'A#m',
  'Bm',
] as const;
export const tomSchema = z.enum(TONS);
export type Tom = z.infer<typeof tomSchema>;

export const TIME_SIGNATURES = ['4/4', '3/4', '6/8', '2/4', '12/8'] as const;

export const createSongSchema = z.object({
  title: z.string().trim().min(2, 'Informe o nome da música').max(200),
  author: z.string().trim().max(200).nullish(),
  baseKey: tomSchema.nullish(),
  bpm: z.coerce.number().int().min(30).max(300).nullish(),
  timeSignature: z.enum(TIME_SIGNATURES).nullish(),
  lyrics: z.string().trim().max(20_000).nullish(),
  chords: z.string().trim().max(20_000).nullish(),
  youtubeUrl: z.string().trim().url('Endereço inválido').max(500).nullish().or(z.literal('')),
  spotifyUrl: z.string().trim().url('Endereço inválido').max(500).nullish().or(z.literal('')),
  notes: z.string().trim().max(1000).nullish(),
  active: z.boolean().default(true),
});
export type CreateSongInput = z.infer<typeof createSongSchema>;

export const updateSongSchema = createSongSchema.partial();
export type UpdateSongInput = z.infer<typeof updateSongSchema>;

export const songSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  author: z.string().nullable(),
  baseKey: z.string().nullable(),
  bpm: z.number().int().nullable(),
  timeSignature: z.string().nullable(),
  lyrics: z.string().nullable(),
  chords: z.string().nullable(),
  youtubeUrl: z.string().nullable(),
  spotifyUrl: z.string().nullable(),
  notes: z.string().nullable(),
  active: z.boolean(),
  /** Quando a igreja cantou pela última vez, e quantas vezes no ano. */
  lastPlayedAt: z.string().datetime().nullable(),
  timesPlayed: z.number().int(),
  filesCount: z.number().int(),
});
export type Song = z.infer<typeof songSchema>;

export const songsQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  includeInactive: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type SongsQuery = z.infer<typeof songsQuerySchema>;

/** Quantos dias sem cantar contam como "acabamos de cantar". */
export const DIAS_DE_ALERTA_DE_REPETICAO = 21;

export const songUsageSchema = z.object({
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  eventStartsAt: z.string().datetime(),
  songKey: z.string().nullable(),
});
export type SongUsage = z.infer<typeof songUsageSchema>;

// ── Arquivos ─────────────────────────────────────────────────────────────────

export const FILE_SCOPES = ['EVENT', 'PLAN_ITEM', 'SONG', 'MINISTRY', 'PERSON'] as const;
export const fileScopeSchema = z.enum(FILE_SCOPES);
export type FileScope = z.infer<typeof fileScopeSchema>;

/** O que a igreja realmente anexa: cifra, slide, vídeo curto, foto, áudio. */
export const TIPOS_DE_ARQUIVO_ACEITOS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'video/mp4',
  'video/webm',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;

export const TAMANHO_MAXIMO_MB = 50;

export const requestUploadSchema = z.object({
  scope: fileScopeSchema,
  /** A que o arquivo se prende. Um só, conforme o escopo. */
  eventId: z.string().uuid().nullish(),
  planItemId: z.string().uuid().nullish(),
  songId: z.string().uuid().nullish(),
  ministryId: z.string().uuid().nullish(),
  personId: z.string().uuid().nullish(),
  filename: z.string().trim().min(1, 'Informe o nome do arquivo').max(255),
  mimeType: z.string().trim().min(3).max(150),
  sizeBytes: z.coerce
    .number()
    .int()
    .min(1, 'Arquivo vazio')
    .max(TAMANHO_MAXIMO_MB * 1024 * 1024, `O limite por arquivo é ${TAMANHO_MAXIMO_MB} MB`),
});
export type RequestUploadInput = z.infer<typeof requestUploadSchema>;

export const uploadTicketSchema = z.object({
  fileId: z.string().uuid(),
  /** Endereço assinado para o navegador enviar direto ao MinIO. */
  uploadUrl: z.string(),
  /** Cabeçalhos que o navegador precisa repetir no PUT. */
  headers: z.record(z.string(), z.string()),
  expiresInSeconds: z.number().int(),
});
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

export const storedFileSchema = z.object({
  id: z.string().uuid(),
  scope: fileScopeSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  createdAt: z.string().datetime(),
  uploadedByName: z.string().nullable(),
});
export type StoredFile = z.infer<typeof storedFileSchema>;

export const storageQuotaSchema = z.object({
  usedBytes: z.number().int(),
  limitBytes: z.number().int(),
  /** Quanto ainda cabe, em bytes. Nunca negativo. */
  freeBytes: z.number().int(),
  percentUsed: z.number(),
});
export type StorageQuota = z.infer<typeof storageQuotaSchema>;

export const filesQuerySchema = z.object({
  scope: fileScopeSchema.optional(),
  eventId: z.string().uuid().optional(),
  planItemId: z.string().uuid().optional(),
  songId: z.string().uuid().optional(),
  ministryId: z.string().uuid().optional(),
  personId: z.string().uuid().optional(),
});
export type FilesQuery = z.infer<typeof filesQuerySchema>;
